import { useMutation, useQuery } from "react-query";
import { fetchPrinters } from "../queries/printer.queries";
import { print } from "../mutations/printer.mutations";
import { useCommand } from "./commands.hooks";
import { useFileContent } from "./file.hooks";
import { useAdminSettings } from "./admin_settings.hooks";
import { useMemo } from "react";
import { usePurchaseOrder } from "./purchase_order.hooks";
import { fetchFileContent } from "../mutations/file.mutations";
import { fetchAdminSettings } from "../queries/admin_settings.queries";
import { fetchCommand } from "../queries/command.queries";
import type { PrintJob } from "../socket";
import { claimPrintJob } from "../mutations/print_job.mutations";
import {
	completePrintJob,
	getPrintClientId,
	releasePrintJob,
	takePrintJob,
} from "../utils/print_jobs";

const DEFAULT_PRINTER_TYPE = "Imprimante";

export const usePrinters = () => {
	return useQuery(["printers"], () => {
		try {
			return fetchPrinters();
		} catch (err) {
			return null;
		}
	});
};

export const useDefaultPrinters = () => {
	const { data: printers } = usePrinters();

	if (Array.isArray(printers)) {
		const printer = printers.find(
			(printer) => printer.type === DEFAULT_PRINTER_TYPE,
		);
		if (printer !== undefined) {
			return { printer };
		}
	}
	return { printer: undefined };
};

export const useUPSPrinters = () => {
	const { data: printers } = usePrinters();
	const printer = useMemo(() => {
		if (Array.isArray(printers)) {
			return printers.find((p) => p.type === "Imprimante UPS");
		}
		return null;
	}, [printers]);

	return { printer };
};

export const useGetPrinterByType = (type: string) => {
	const { data: printers } = usePrinters();
	const printer = useMemo(() => {
		if (Array.isArray(printers)) {
			return printers.find((p) => p.type === type);
		}
		return null;
	}, [printers, type]);

	return { printer };
};

export const usePrintCommandPurchaseOrderFile = ({
	id,
}: {
	id: number | string | null;
}) => {
	// 1. Reload command
	const { data: command } = useCommand(id);
	// 2. Download file content
	const { data: content } = useFileContent({
		id: command?.purchaseOrderFileId ?? undefined,
	});
	// 3. Get printing service address
	const { data: adminSettings } = useAdminSettings();
	// 4. Get default printer
	const { printer } = useDefaultPrinters();
	const { mutate: mPrint } = useMutation(print);
	// 3. Generate form data
	const printCommandPurchaseOrder = (
		options?: object,
		title: string = "test",
	) => {
		const form = new FormData();
		if (printer?.label === undefined) {
			return;
		}
		form.append("name", printer?.label);
		form.append("title", title);
		form.append(
			"options",
			options
				? JSON.stringify({ "orientation-requested": "4", ...options })
				: '{"orientation-requested": "4"}',
		);
		const blob = new Blob([content]);
		form.append("file", blob);
		if (adminSettings?.printingServiceAddress) {
			mPrint({ host: adminSettings?.printingServiceAddress, form });
		}
	};
	return { printCommandPurchaseOrder };
};

export const usePrintPageCommandPurchaseOrderFile = ({
	id,
}: {
	id: number | string | null;
}) => {
	// 1. Reload command
	const { data: command } = useCommand(id);
	// 2. Download file content
	const { data: content } = useFileContent({
		id: command?.purchaseOrderFileId ?? undefined,
	});
	// 3. Generate form data
	const printPageCommandPurchaseOrder = () => {
		if (content === undefined) {
			return;
		}
		const pdfContent = new Blob([content], { type: "application/pdf" });
		const pdfUrl = URL.createObjectURL(pdfContent);
		window.open(pdfUrl);
	};
	return { printPageCommandPurchaseOrder };
};

export const usePrintUPSLabelFile = ({
	id,
}: {
	id: number | string | null;
}) => {
	// 1. Reload command
	const { data: purchaseOrder } = usePurchaseOrder({ id: id ?? undefined });
	// 2. Download file content
	const { data: content } = useFileContent({
		id: purchaseOrder?.shippingLabelFileId ?? undefined,
	});
	// 3. Get printing service address
	const { data: adminSettings } = useAdminSettings();
	// 4. Get default printer
	const { printer } = useUPSPrinters();
	const { mutate: mPrint } = useMutation(print);
	// 3. Generate form data
	const printUPSLabelFile = (options?: object, title: string = "test") => {
		const form = new FormData();
		if (printer?.label === undefined) {
			return;
		}
		form.append("name", printer?.label);
		form.append("title", title);
		form.append("options", options ? JSON.stringify(options) : "{}");
		const blob = new Blob([content]);
		form.append("file", blob);
		if (adminSettings?.printingServiceAddress) {
			mPrint({ host: adminSettings?.printingServiceAddress, form });
		}
	};
	return { printUPSLabelFile };
};

export const printFileById = async (
	fileId: number,
	printerType: string,
	title: string = "test",
	options?: object,
) => {
	const adminSettings = await fetchAdminSettings();
	if (!adminSettings) {
		console.error("admin settings not found");
		return;
	}

	const printer = (await fetchPrinters()).find((p) => p.type === printerType);
	if (!printer) {
		console.error("printer not found");
		return;
	}

	if (!fileId) {
		console.error("fileId not found");
		return;
	}
	const content = await fetchFileContent(fileId);
	if (!content) {
		console.error("file not found");
		return;
	}

	const form = new FormData();
	if (printer?.label === undefined) {
		return;
	}
	form.append("name", printer?.label);
	form.append("title", title);
	form.append("options", options ? JSON.stringify(options) : "{}");
	const blob = new Blob([content]);

	form.append("file", blob);
	if (adminSettings?.printingServiceAddress) {
		await print({ host: adminSettings?.printingServiceAddress, form });
	}
};

export const triggerPrintJobs = async (jobs: PrintJob[]) => {
	for (const job of jobs) {
		if (takePrintJob(job.jobKey) === false) {
			continue;
		}

		try {
			const claimed = await claimPrintJob({
				clientId: getPrintClientId(),
				jobKey: job.jobKey,
			});
			if (claimed === false) {
				releasePrintJob(job.jobKey);
				continue;
			}

			await printFileById(
				job.fileId,
				job.printerType,
				job.title,
				job.options,
			);
			completePrintJob(job.jobKey);
		} catch (err) {
			releasePrintJob(job.jobKey);
			throw err;
		}
	}
};

export const printLabels = async (
	commandsId: number[],
	printerType: string,
	title: string = "test",
	options?: object,
) => {
	const adminSettings = await fetchAdminSettings();
	if (!adminSettings) {
		console.error("admin settings not found");
		return;
	}

	const printer = (await fetchPrinters()).find((p) => p.type === printerType);
	if (!printer) {
		console.error("printer not found");
		return;
	}

	commandsId.forEach(async (id) => {
		const command = await fetchCommand(id);
		if (!command) {
			console.error("command not found");
			return;
		}
		if (!command.labelFileId) {
			console.error("purchase order not found");
			return;
		}
		const content = await fetchFileContent(command.labelFileId);
		if (!content) {
			console.error("file not found");
			return;
		}

		const form = new FormData();
		if (printer?.label === undefined) {
			return;
		}
		form.append("name", printer?.label);
		form.append("title", title);
		form.append("options", options ? JSON.stringify(options) : "{}");
		const blob = new Blob([content]);
		form.append("file", blob);
		if (adminSettings?.printingServiceAddress) {
			print({ host: adminSettings?.printingServiceAddress, form });
		}
	});
};
